import { CommonModule } from "@angular/common";
import { Component, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatDialogModule } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatNativeDateModule } from "@angular/material/core";
import { MatSelectModule } from "@angular/material/select";

import {
  IncidenciaSeguridad,
  IncidenciaSeguridadRevisionMensual,
} from "../../shared/model/iso42001/incidencia-seguridad.model";
import { ConfigTableroIso, GestionIncidenciasIsoBase } from "../../shared/incidencias-iso/gestion-incidencias-iso-base";
import { etiquetaOpcion } from "../../shared/incidencias-iso/incidencias-iso.comun";
import { crearColumnasTablero } from "../../shared/incidencias-iso/tablero-incidencias-base";
import { AsignarResponsableSeguridadComponent } from "../asignar-responsable-seguridad/asignar-responsable-seguridad.component";
import { DetalleIncidenciaSeguridadComponent } from "../detalle-incidencia-seguridad/detalle-incidencia-seguridad.component";
import {
  GRAVEDAD_OPTIONS,
  INCIDENCIA_SEGURIDAD_COLUMNS,
  IncidenciaSeguridadColumn,
  PRIORIDAD_OPTIONS,
  TIPO_EVENTO_OPTIONS,
} from "../incidencias-seguridad.constants";
import { IncidenciaSeguridadFormComponent } from "../incidencia-seguridad-form/incidencia-seguridad-form.component";
import { IncidenciasSeguridadService } from "../incidencias-seguridad.service";
import { RevisionMensualSeguridadDialogComponent } from "../revision-mensual-seguridad-dialog/revision-mensual-seguridad-dialog.component";

@Component({
  selector: "app-gestion-incidencias-seguridad",
  standalone: true,
  templateUrl: "./gestion-incidencias-seguridad.component.html",
  styleUrls: ["./gestion-incidencias-seguridad.component.css"],
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatDatepickerModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatNativeDateModule,
    MatSelectModule,
  ],
})
export class GestionIncidenciasSeguridadComponent extends GestionIncidenciasIsoBase<IncidenciaSeguridadColumn, IncidenciaSeguridad> {
  readonly tipoEventoOptions = TIPO_EVENTO_OPTIONS;
  readonly gravedadOptions = GRAVEDAD_OPTIONS;
  readonly prioridadOptions = PRIORIDAD_OPTIONS;
  readonly columns = crearColumnasTablero<IncidenciaSeguridadColumn, IncidenciaSeguridad>(INCIDENCIA_SEGURIDAD_COLUMNS);

  historialPopupOpen = false;
  historialLoading = false;
  historialDetalleLoading = false;
  historialError = "";
  historialAnios: number[] = [];
  anioHistorialSeleccionado?: number;
  revisionesMensuales: IncidenciaSeguridadRevisionMensual[] = [];
  revisionHistorialSeleccionada?: IncidenciaSeguridadRevisionMensual;
  incidenciasHistorialSeleccionado: IncidenciaSeguridad[] = [];
  puedeRegistrarRevisionMensual = false;
  periodoRevisionMensual = "";

  protected readonly incidenciasService = inject(IncidenciasSeguridadService);
  protected readonly config: ConfigTableroIso = {
    ruta: "/INCIDENCIAS/INC_SEGURIDAD",
    prefijoDialogo: "incidencia-seguridad",
    errorConsulta: "No fue posible cargar las incidencias de seguridad.",
    componenteDetalle: DetalleIncidenciaSeguridadComponent,
    componenteAsignacion: AsignarResponsableSeguridadComponent,
  };
  private ventanaRevisionMensual: any;

  trackByRevision(_index: number, revision: IncidenciaSeguridadRevisionMensual): number {
    return revision.idRevisionMensual ?? Number(`${revision.anioRevision || 0}${revision.mesRevision || 0}`);
  }

  abrirAlta(): void {
    const ref = this.dialog.open(IncidenciaSeguridadFormComponent, {
      disableClose: false,
      autoFocus: true,
      width: "80vw",
      maxWidth: "80vw",
      maxHeight: "75vh",
      panelClass: ["custom-modalbox", "incidencia-seguridad-dialog", "incidencia-seguridad-alta-dialog"],
      data: { mode: "alta" },
    });
    ref.afterClosed().subscribe(() => this.refrescarTodas());
  }

  abrirRevisionMensual(): void {
    const ref = this.dialog.open(RevisionMensualSeguridadDialogComponent, {
      disableClose: false,
      autoFocus: true,
      width: "560px",
      maxWidth: "92vw",
      maxHeight: "78vh",
      panelClass: ["custom-modalbox", "incidencia-seguridad-dialog", "incidencia-seguridad-revision-dialog"],
      data: { ventana: this.ventanaRevisionMensual },
    });
    ref.afterClosed().subscribe(() => {
      this.cargarVentanaRevisionMensual();
      if (this.historialPopupOpen) {
        this.cargarHistorialRevisiones();
      }
      this.refrescarTodas();
    });
  }

  abrirHistorialRevisionMensual(): void {
    this.historialPopupOpen = !this.historialPopupOpen;
    this.filtersPopupOpen = false;
    if (this.historialPopupOpen && !this.historialAnios.length) {
      this.cargarHistorialRevisiones();
    }
  }

  onTipoChange(): void {
    this.reiniciarPaginas();
    this.refrescarTodas();
  }

  onAnioHistorialChange(anio: number): void {
    this.anioHistorialSeleccionado = Number(anio);
    this.revisionHistorialSeleccionada = undefined;
    this.incidenciasHistorialSeleccionado = [];
    this.cargarHistorialRevisiones(true);
  }

  seleccionarRevisionHistorial(revision: IncidenciaSeguridadRevisionMensual): void {
    if (!revision.mesRevision || !revision.anioRevision) {
      return;
    }
    this.historialDetalleLoading = true;
    this.historialError = "";
    this.revisionHistorialSeleccionada = revision;
    this.incidenciasHistorialSeleccionado = [];
    this.incidenciasService.consultarRevisionMensual(revision.mesRevision, revision.anioRevision).subscribe({
      next: (res) => {
        if (!res || res.codigoError) {
          this.historialError = res?.mensajeError || "No fue posible consultar el detalle de la revisión.";
          return;
        }
        this.revisionHistorialSeleccionada = res.revisionMensual || revision;
        this.incidenciasHistorialSeleccionado = res.incidencias || [];
      },
      error: (e) => {
        this.historialError = e?.error?.mensajeError || e?.error?.message || "No fue posible consultar el detalle de la revisión.";
      },
      complete: () => {
        this.historialDetalleLoading = false;
      },
    });
  }

  labelTipo(value?: string): string {
    return etiquetaOpcion(value, this.tipoEventoOptions);
  }

  labelMes(value?: number): string {
    const meses = [
      "",
      "Enero",
      "Febrero",
      "Marzo",
      "Abril",
      "Mayo",
      "Junio",
      "Julio",
      "Agosto",
      "Septiembre",
      "Octubre",
      "Noviembre",
      "Diciembre",
    ];
    return value ? meses[value] || "-" : "-";
  }

  /** Además del tablero, consulta si ya se puede registrar la revisión mensual. */
  protected override cargarInicio(): void {
    super.cargarInicio();
    this.cargarVentanaRevisionMensual();
  }

  protected abrirFormularioDesdeDetalle(action: "edicion" | "seguimiento", incidencia: IncidenciaSeguridad): void {
    if (!incidencia?.idIncidenciaIa) {
      return;
    }
    const esEdicionApertura = action === "edicion" && incidencia.estadoEvento === "ABIERTO";
    const ref = this.dialog.open(IncidenciaSeguridadFormComponent, {
      disableClose: false,
      autoFocus: true,
      width: "80vw",
      ...(esEdicionApertura ? {} : { height: "75vh" }),
      maxWidth: "80vw",
      maxHeight: "75vh",
      panelClass: [
        "custom-modalbox",
        "incidencia-seguridad-dialog",
        ...(esEdicionApertura ? ["incidencia-seguridad-alta-dialog"] : []),
      ],
      data: {
        mode: action,
        idIncidenciaIa: incidencia.idIncidenciaIa,
        incidencia,
      },
    });
    ref.afterClosed().subscribe(() => this.refrescarTodas());
  }

  private cargarHistorialRevisiones(forzarAnio = false): void {
    this.historialLoading = true;
    this.historialError = "";
    const anio = forzarAnio ? this.anioHistorialSeleccionado : undefined;
    this.incidenciasService.consultarRevisionMensual(undefined, anio).subscribe({
      next: (res) => {
        if (!res || res.codigoError) {
          this.historialError = res?.mensajeError || "No fue posible cargar el historial de revisiones.";
          this.revisionesMensuales = [];
          return;
        }
        const revisiones = res.revisionesMensuales || [];
        if (!forzarAnio) {
          this.historialAnios = Array.from(
            new Set(revisiones.map((revision) => revision.anioRevision).filter((anioRevision): anioRevision is number => !!anioRevision)),
          ).sort((a, b) => b - a);
          const anioActual = new Date().getFullYear();
          this.anioHistorialSeleccionado = this.historialAnios.includes(anioActual)
            ? anioActual
            : this.historialAnios[0];
          this.revisionesMensuales = this.anioHistorialSeleccionado
            ? revisiones.filter((revision) => revision.anioRevision === this.anioHistorialSeleccionado)
            : [];
        } else {
          this.revisionesMensuales = revisiones;
        }
        this.revisionHistorialSeleccionada = undefined;
        this.incidenciasHistorialSeleccionado = [];
      },
      error: (e) => {
        this.historialError = e?.error?.mensajeError || e?.error?.message || "No fue posible cargar el historial de revisiones.";
        this.revisionesMensuales = [];
      },
      complete: () => {
        this.historialLoading = false;
      },
    });
  }

  private cargarVentanaRevisionMensual(): void {
    this.incidenciasService.obtenerVentanaRevisionMensual().subscribe({
      next: (res) => {
        if (!res || res.codigoError) {
          this.puedeRegistrarRevisionMensual = false;
          this.ventanaRevisionMensual = undefined;
          return;
        }
        this.puedeRegistrarRevisionMensual = !!res.puedeRegistrarRevisionMensual;
        this.periodoRevisionMensual = res.periodoRevision || "";
        this.ventanaRevisionMensual = res;
      },
      error: () => {
        this.puedeRegistrarRevisionMensual = false;
        this.ventanaRevisionMensual = undefined;
      },
    });
  }
}
